import { useSuspenseQuery } from '@tanstack/react-query';
import * as React from 'react';

import * as api from '@gitbook/api-client';
import type { NavigationScreenSpaceState } from '@gitbook/app-navigation';
import {
    createPalette,
    type PaletteCommandDef,
    PalettePositionMaxHeight,
    PalettePositionWidth,
    usePaletteTrigger,
} from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { BreadcrumbButton, toast } from '@gitbook/spine-react';
import { assert, filterOutNullable } from '@gitbook/util-ts';

import { useSpaceActionsCommands, useSpaceSites } from '../../../Components';
import { useNavigation } from '../../../navigation';
import { apiClient, apiQuery } from '../../../state';
import { useExportPDFCommand } from '../utils';

/**
 * Register commands relative to the space.
 */
export function SpaceCommandsButton(props: {
    space: api.Space;
    screen: NavigationScreenSpaceState;
}) {
    const { space } = props;

    const { data: organizationContent } = useSuspenseQuery(
        apiQuery('getOrganizationAllContent', {
            organizationId: space.organization,
        })
    );

    const { navigate } = useNavigation();
    const spaceActions = useSpaceActionsCommands(space);

    const sites = useSpaceSites(organizationContent, space.id);
    const hasPublishedSite = sites.some((site) => site.site.published);

    const exportPDF = useExportPDFCommand({ space });

    const paletteCommands = React.useMemo(() => {
        return {
            // Edit mode (only when the space doesn't have a git sync or not part of a published site)
            'space.lockEdits': (() => {
                // Display nothing if the user has no admin permissions.
                if (!space.permissions.admin) {
                    return null;
                }

                if (space.editMode === api.SpaceEditMode.Live) {
                    return {
                        key: 'space.lockEdits',
                        icon: Icon.LiveEditsOff,
                        title: 'Lock live edits',
                        trigger: async () => {
                            await lockLiveEdits({ space });
                        },
                    };
                }

                const reason = (() => {
                    if (space.gitSync) {
                        return 'Git Sync is enabled';
                    }
                    if (hasPublishedSite) {
                        return 'Space is part of a published site';
                    }
                    return null;
                })();

                const disabledProps = reason
                    ? {
                          disabled: true,
                          subtitle: reason,
                          title: 'Live edits locked',
                      }
                    : {};

                return {
                    key: 'space.unlockEdits',
                    icon: Icon.LiveEditsOn,
                    title: 'Unlock live edits',
                    trigger: async () => {
                        await apiClient.spaces.updateSpaceById(space.id, {
                            editMode: api.SpaceEditMode.Live,
                        });

                        toast('Unlocked live edits', {
                            description: 'Changes will be saved automatically.',
                            icon: Icon.UnLock,
                        });
                    },
                    ...disabledProps,
                };
            })(),

            'space.share': space.permissions.admin
                ? {
                      key: 'space.share',
                      icon: Icon.UserAdd,
                      title: 'Share',
                      descendant: () => {
                          return createPalette({
                              static: true,
                              groups: [
                                  {
                                      key: 'space.share.options',
                                      commands: [
                                          {
                                              key: 'space.share.invite',
                                              title: 'With teammates…',
                                              icon: Icon.UserAdd,
                                              trigger: () => {
                                                  navigate({
                                                      current: {
                                                          sideSheet: {
                                                              type: 'share',
                                                              tab: 'invite',
                                                          },
                                                      },
                                                  });
                                              },
                                          },
                                          {
                                              key: 'space.share.publish',
                                              title: 'On the web…',
                                              icon: Icon.Globe,
                                              trigger: () => {
                                                  navigate({
                                                      current: {
                                                          sideSheet: {
                                                              type: 'share',
                                                              tab: 'docs-sites',
                                                          },
                                                      },
                                                  });
                                              },
                                          },
                                      ],
                                  },
                              ],
                          });
                      },
                  }
                : null,

            // Sync with GitHub
            'space.configureGitSync': space.permissions.admin
                ? {
                      key: 'space.configureGitSync',
                      icon: Icon.GitHub,
                      title: space.gitSync?.installationProvider
                          ? 'Configure Git Sync'
                          : 'Set up Git Sync',
                      trigger: () => {
                          // existing configuration, go to integration screen in context
                          if (space.gitSync?.integration) {
                              navigate({
                                  current: {
                                      sideSheet: {
                                          type: 'integrations',
                                          view: {
                                              type: 'integration',
                                              integration: space.gitSync.integration,
                                          },
                                      },
                                  },
                              });
                              return;
                          }

                          // new installation, go to flow in context
                          navigate({
                              current: { sideSheet: { type: 'gitsync-setup' } },
                          });
                      },
                  }
                : null,

            'space.mergeRuless': space.permissions.admin
                ? {
                      key: 'space.mergeRuless',
                      icon: Icon.MergeRulesAllow,
                      title: 'Merge rules',
                      trigger: () => {
                          navigate({
                              screen: {
                                  type: 'space',
                                  space: space.id,
                                  sideSheet: { type: 'merge-rule' },
                              },
                          });
                      },
                  }
                : null,

            'space.permissions': space.permissions.admin
                ? {
                      key: 'space.permissions',
                      icon: Icon.Users,
                      title: 'Permissions',
                      trigger: () => {
                          navigate({
                              screen: {
                                  type: 'space',
                                  space: space.id,
                                  sideSheet: {
                                      type: 'share',
                                      tab: 'invite',
                                  },
                              },
                          });
                      },
                  }
                : null,
            'space.comments': space.permissions.comment
                ? {
                      key: 'space.comments',
                      icon: Icon.Comment,
                      title: 'Comments',
                      trigger: () => {
                          navigate({
                              screen: {
                                  type: 'space',
                                  space: space.id,
                                  sideSheet: {
                                      type: 'comments',
                                  },
                              },
                          });
                      },
                  }
                : null,
            'space.history':
                // https://linear.app/gitbook-x/issue/RND-1988/explore-allowing-readers-to-access-history-and-change-requests
                space.permissions.edit
                    ? {
                          key: 'space.history',
                          icon: Icon.History,
                          title: 'Version history',
                          trigger: () => {
                              navigate({
                                  screen: {
                                      type: 'space',
                                      space: space.id,
                                      revision: space.revision,
                                      mode: 'diff',
                                  },
                              });
                          },
                      }
                    : null,
        } satisfies Record<string, PaletteCommandDef | null>;
    }, [space, navigate, hasPublishedSite]);

    const basicCommands = (() => {
        const group = spaceActions.paletteGroup();
        assert(Array.isArray(group.commands));
        return group.commands;
    })();

    const commands = [...basicCommands, ...Object.values(paletteCommands)].filter(
        filterOutNullable
    );

    const paletteTrigger = usePaletteTrigger({
        palette: () =>
            createPalette({
                position: {
                    width: PalettePositionWidth.MEDIUM,
                    maxHeight: PalettePositionMaxHeight.AUTO,
                    preferredPlacement: 'bottom',
                },
                groups: [
                    {
                        key: 'space.content',
                        commands: [paletteCommands['space.lockEdits']].filter(filterOutNullable),
                    },
                    {
                        key: 'space.importExport',
                        commands: [
                            paletteCommands['space.share'],
                            paletteCommands['space.configureGitSync'],
                            paletteCommands['space.mergeRuless'],
                            paletteCommands['space.import'],
                            exportPDF,
                        ].filter(filterOutNullable),
                    },
                    {
                        key: 'space.navigation',
                        commands: [
                            paletteCommands['space.history'],
                            paletteCommands['space.comments'],
                        ].filter(filterOutNullable),
                    },
                    {
                        key: 'space.basic',
                        commands: basicCommands,
                    },
                ].filter(filterOutNullable),
            }),
    });

    if (!commands.length) {
        return null;
    }

    return (
        <>
            <BreadcrumbButton
                label="Space actions"
                iconOnly
                icon={Icon.MoreHorizontal}
                isActive={false}
                {...paletteTrigger.triggerProps}
            />
            {spaceActions?.elements}
        </>
    );
}

/**
 * Lock the live edits on the space, by first flushing realtime changes.
 * The operation is indicated to the user using toasts.
 */
async function lockLiveEdits({ space }: { space: api.Space }) {
    await toast.catch(async () => {
        await apiClient.spaces.updateSpaceById(space.id, {
            editMode: api.SpaceEditMode.Locked,
        });
        toast('Locked live edits', {
            description: `You'll need to use change requests to modify this space.`,
            icon: Icon.Lock,
        });
    }, 'Error while locking live edits');
}
